import React from "react";
import { Link } from "react-router-dom";
import { Label } from "../../misc/Label";
import { Switch } from "../../misc/Switch";
import { Button } from "../../misc/Button";
import SimpleTooltip from "../../misc/SimpleTooltip";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { PlatformIcon } from "../../../utils/logo-getter";
import {
    AutopilotMode,
    StagedAutopilotSettings as StagedSettings,
} from "../../../types/autopilot";

// MB-1398: the autopilot settings form fields, extracted from the Settings
// page so the MLS setup wizard renders the exact same surface. Pure
// controlled component — fetch/persist/confirm logic stays with the caller.

export const ALL_AUTOPILOT_CHANNELS = [
    "Facebook",
    "Instagram",
    "Twitter",
    "LinkedIn",
    "Pinterest",
    "Google",
    "YouTube",
];

const CHANNEL_LABELS: Record<string, string> = {
    Google: "Google Business",
    Twitter: "X / Twitter",
};

export const channelLabel = (channel: string): string =>
    CHANNEL_LABELS[channel] ?? channel;

export const MODE_OPTIONS: {
    id: AutopilotMode;
    title: string;
    description: string;
}[] = [
    {
        id: "warmup",
        title: "Warm-Up",
        description:
            "A gentle ramp for accounts without much posting history. Builds platform trust first; automatically graduates to Cruise Control after 4 weeks.",
    },
    {
        id: "cruise",
        title: "Cruise Control",
        description:
            "The recommended steady pace — every listing marketed weekly on your main channels without overwhelming your audience.",
    },
    {
        id: "fullthrottle",
        title: "Full Throttle",
        description:
            "Maximum sustainable output for very active accounts and heavy listing loads.",
    },
];

interface AutopilotSettingsFieldsProps {
    staged: StagedSettings;
    onChange: (next: StagedSettings) => void;
    connectedChannels: string[];
    unconnectedChannels: string[];
    disabled?: boolean;
    // Hide the master switch (the wizard renders its own step-level toggle).
    hideEnableSwitch?: boolean;
    enableSwitchDisabled?: boolean;
    // Shown as a tooltip on the disabled master switch (MB-1532: busy setup).
    enableSwitchDisabledReason?: string;
    // The wizard suppresses in-form navigation links (no leaving the flow).
    showConnectionsLink?: boolean;
    // MB-1481: the Settings → Commercial section is now the switch's one
    // Settings home, so the Settings → Autopilot page hides it here. Default
    // false (shown) keeps the MLS setup wizard's step 3 unchanged.
    hideCommercialSwitch?: boolean;
}

const AutopilotSettingsFields: React.FC<AutopilotSettingsFieldsProps> = ({
    staged,
    onChange,
    connectedChannels,
    unconnectedChannels,
    disabled = false,
    hideEnableSwitch = false,
    enableSwitchDisabled = false,
    enableSwitchDisabledReason,
    showConnectionsLink = true,
    hideCommercialSwitch = false,
}) => {
    const isMobile = useIsMobile(1200);

    const toggleChannel = (channel: string) => {
        const current = staged.channels || [];
        onChange({
            ...staged,
            channels: current.includes(channel)
                ? current.filter((c) => c !== channel)
                : [...current, channel],
        });
    };

    return (
        <>
            {!hideEnableSwitch && (
                <div className="flex items-center justify-between">
                    <div className="flex flex-col flex-[0.8]">
                        <Label
                            htmlFor="autopilot-enabled"
                            className="font-semibold text-primary"
                        >
                            Auto Pilot
                        </Label>
                        <span className="text-sm text-neutral-500 mt-2">
                            {staged.enabled
                                ? "On — every active listing gets a campaign automatically, and posts publish without approval."
                                : "Off — you create and manage campaigns manually."}
                        </span>
                    </div>
                    <SimpleTooltip
                        message={enableSwitchDisabledReason ?? ""}
                        visible={Boolean(enableSwitchDisabledReason)}
                    >
                        <Switch
                            id="autopilot-enabled"
                            checked={staged.enabled}
                            disabled={enableSwitchDisabled}
                            // A disabled control swallows hover, so the
                            // tooltip wrapper has to receive it instead.
                            className={
                                enableSwitchDisabledReason
                                    ? "pointer-events-none"
                                    : undefined
                            }
                            onClick={() =>
                                onChange({ ...staged, enabled: !staged.enabled })
                            }
                        />
                    </SimpleTooltip>
                </div>
            )}

            {/* Mode */}
            <div>
                <Label className="font-semibold text-primary">Pace</Label>
                <div
                    className={`mt-2 grid gap-3 ${
                        isMobile ? "grid-cols-1" : "grid-cols-3"
                    }`}
                >
                    {MODE_OPTIONS.map((option) => (
                        <button
                            key={option.id}
                            type="button"
                            disabled={disabled}
                            onClick={() =>
                                onChange({ ...staged, mode: option.id })
                            }
                            className={`text-left border rounded-md p-3 transition-colors ${
                                staged.mode === option.id
                                    ? "border-primary bg-primary/5"
                                    : "border-gray-200 hover:border-gray-300"
                            }`}
                        >
                            <span className="font-medium text-sm text-primary">
                                {option.title}
                            </span>
                            <p className="text-xs text-neutral-500 mt-1">
                                {option.description}
                            </p>
                        </button>
                    ))}
                </div>
            </div>

            {/* Channels */}
            <div>
                <Label className="font-semibold text-primary">Channels</Label>
                <span className="block text-sm text-neutral-500 mt-1">
                    Every channel you have connected is on by default —
                    deselect any you want Auto Pilot to leave alone.
                </span>
                <div className="mt-2 flex flex-wrap gap-2">
                    {connectedChannels.map((channel) => {
                        const selected = staged.channels.includes(channel);
                        return (
                            <button
                                key={channel}
                                type="button"
                                disabled={disabled}
                                onClick={() => toggleChannel(channel)}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-sm ${
                                    selected
                                        ? "border-primary bg-primary/5 text-primary font-medium"
                                        : "border-gray-200 bg-white text-neutral-400"
                                }`}
                            >
                                <span
                                    className={
                                        selected ? "" : "grayscale opacity-40"
                                    }
                                >
                                    <PlatformIcon
                                        platform={channel}
                                        className="w-4 h-4"
                                    />
                                </span>
                                {channelLabel(channel)}
                            </button>
                        );
                    })}
                </div>
                {unconnectedChannels.length > 0 && (
                    <p className="text-sm text-neutral-500 mt-3">
                        Not connected:{" "}
                        {unconnectedChannels.map(channelLabel).join(", ")}.{" "}
                        {showConnectionsLink ? (
                            <>
                                <Link
                                    to="/settings/connections"
                                    className="text-primary underline"
                                >
                                    Connect a channel
                                </Link>{" "}
                                to make it available to Auto Pilot.
                            </>
                        ) : (
                            <>
                                Connect a channel from Settings →
                                Connections to make it available to
                                Auto Pilot.
                            </>
                        )}
                    </p>
                )}
            </div>

            {/* Campaign defaults */}
            <div className="space-y-4">
                <Label className="font-semibold text-primary">
                    Campaign defaults
                </Label>
                <div className="flex items-center justify-between">
                    <div className="flex flex-col flex-[0.8]">
                        <Label
                            htmlFor="open-house-sync"
                            className="text-primary"
                        >
                            MLS Open House Sync
                        </Label>
                        <span className="text-sm text-neutral-500">
                            Upcoming open houses from the MLS are highlighted
                            in your scheduled posts.
                        </span>
                    </div>
                    <Switch
                        id="open-house-sync"
                        checked={staged.openHouseSync}
                        disabled={disabled}
                        onClick={() =>
                            onChange({
                                ...staged,
                                openHouseSync: !staged.openHouseSync,
                            })
                        }
                    />
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex flex-col flex-[0.8]">
                        <Label htmlFor="front-property" className="text-primary">
                            Display Front of Property in all Posts
                        </Label>
                        <span className="text-sm text-neutral-500">
                            A front exterior photo leads every post (Facebook,
                            LinkedIn, X, Google Business).
                        </span>
                    </div>
                    <Switch
                        id="front-property"
                        checked={staged.frontOfProperty}
                        disabled={disabled}
                        onClick={() =>
                            onChange({
                                ...staged,
                                frontOfProperty: !staged.frontOfProperty,
                            })
                        }
                    />
                </div>
                <div className="flex items-center justify-between">
                    <div className="flex flex-col flex-[0.8]">
                        <Label htmlFor="floor-plan" className="text-primary">
                            Dedicated Floor Plan Posts
                        </Label>
                        <span className="text-sm text-neutral-500">
                            Create posts specifically showcasing floor plan
                            images when a listing has them.
                        </span>
                    </div>
                    <Switch
                        id="floor-plan"
                        checked={staged.floorPlanPosts}
                        disabled={disabled}
                        onClick={() =>
                            onChange({
                                ...staged,
                                floorPlanPosts: !staged.floorPlanPosts,
                            })
                        }
                    />
                </div>
                {!hideCommercialSwitch && (
                    <div className="flex items-center justify-between">
                        <div className="flex flex-col flex-[0.8]">
                            <Label
                                htmlFor="commercial-use-price"
                                className="text-primary"
                            >
                                Include pricing in commercial posts
                            </Label>
                            <span className="text-sm text-neutral-500">
                                Applies to Auto Pilot campaigns on commercial
                                listings at their next weekly renewal.
                            </span>
                        </div>
                        <Switch
                            id="commercial-use-price"
                            checked={staged.commercialUsePrice !== false}
                            disabled={disabled}
                            onClick={() =>
                                onChange({
                                    ...staged,
                                    commercialUsePrice:
                                        !(staged.commercialUsePrice !== false),
                                })
                            }
                        />
                    </div>
                )}
                <div className="flex items-center justify-between">
                    <div className="flex flex-col flex-[0.8]">
                        <Label className="text-primary">
                            Call to Action Placement
                        </Label>
                        <span className="text-sm text-neutral-500">
                            Where the call to action appears in your posts.
                        </span>
                    </div>
                    <div className="flex space-x-2">
                        <Button
                            variant={
                                staged.ctaPlacement === "top" ? "green" : "outv3"
                            }
                            className="w-[80px]"
                            size="sm"
                            disabled={disabled}
                            onClick={() =>
                                onChange({ ...staged, ctaPlacement: "top" })
                            }
                        >
                            Top
                        </Button>
                        <Button
                            variant={
                                staged.ctaPlacement === "bottom"
                                    ? "green"
                                    : "outv3"
                            }
                            className="w-[80px]"
                            size="sm"
                            disabled={disabled}
                            onClick={() =>
                                onChange({ ...staged, ctaPlacement: "bottom" })
                            }
                        >
                            Bottom
                        </Button>
                    </div>
                </div>
            </div>

            {/* Listing priority */}
            <div className="flex items-center justify-between">
                <div className="flex flex-col flex-[0.8]">
                    <Label className="font-semibold text-primary">
                        Prioritize listings by
                    </Label>
                    <span className="text-sm text-neutral-500 mt-1">
                        When channels have limited weekly slots, this decides
                        which listings lead. Every listing still gets covered.
                    </span>
                </div>
                <div className="flex space-x-2">
                    <Button
                        variant={
                            staged.listingPriority === "price"
                                ? "green"
                                : "outv3"
                        }
                        size="sm"
                        disabled={disabled}
                        onClick={() =>
                            onChange({ ...staged, listingPriority: "price" })
                        }
                    >
                        Highest price
                    </Button>
                    <Button
                        variant={
                            staged.listingPriority === "newest"
                                ? "green"
                                : "outv3"
                        }
                        size="sm"
                        disabled={disabled}
                        onClick={() =>
                            onChange({ ...staged, listingPriority: "newest" })
                        }
                    >
                        Newest first
                    </Button>
                </div>
            </div>
        </>
    );
};

export default AutopilotSettingsFields;
